import {Modal, ModalClose, ModalDialog, Slider, DialogTitle, DialogContent, Divider, FormControl, FormLabel, Input, Select, Option, Alert} from "@mui/joy";
import React, {useEffect, useMemo, useState} from "react";
import Button from "@mui/joy/Button";
import DialogActions from '@mui/joy/DialogActions';
import Typography from "@mui/joy/Typography";
import Box from "@mui/joy/Box";
import {Elements, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {toast} from "react-toastify";
import GeneralApiService from "../../axios/Axios";
import {stripePromise} from "../../stripeConfig";
import {Info} from "@mui/icons-material";

const formatGBP = (amount) => {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

function PaymentForm({clientSecret, amount, onSuccess}) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!stripe || !elements) return;
        setSubmitting(true);
        const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: window.location.href
            },
            redirect: "if_required"
        });
        if (result.error) {
            toast.error(result.error.message || "Payment failed. Please try again.");
            setSubmitting(false);
            return;
        }
        if (result.paymentIntent && (result.paymentIntent.status === "succeeded" || result.paymentIntent.status === "processing")) {
            toast.success("Payment submitted successfully.");
            onSuccess && onSuccess();
        }
        setSubmitting(false);
    };

    return (
        <>
            <PaymentElement options={{layout: "tabs"}}/>
            <DialogActions>
                <Button
                    fullWidth
                    color="success"
                    loading={submitting || !stripe || !elements}
                    onClick={handleSubmit}
                >
                    Pay {formatGBP(amount)}
                </Button>
            </DialogActions>
        </>
    );
}

interface PaymentMethod { id: string; type?: string; brand: string; last4: string; email?: string; exp_month: number | null; exp_year: number | null; default: boolean }

export default function StripeBalanceTopUpModal({openState, franchise, parentCallback, onSaved}) {
    const [value, setValue] = useState<number>(100);
    const [open, setOpen] = useState(openState);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [loadingIntent, setLoadingIntent] = useState(false);
    const [confirmedAmount, setConfirmedAmount] = useState<number | null>(null);
    const [autoRechargeThreshold, setAutoRechargeThreshold] = useState<number>(50);
    const [autoRechargeAmount, setAutoRechargeAmount] = useState<number>(300);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null);
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
    const [savingAutoRecharge, setSavingAutoRecharge] = useState(false);

    useEffect(() => {
        setOpen(openState);
    }, [openState]);

    useEffect(() => {
        if (open && franchise?.stripe_customer_account_id) {
            GeneralApiService.getStripePaymentMethods()
                .then((res: { payment_methods: PaymentMethod[]; default_payment_method: string | null }) => {
                    setPaymentMethods(res.payment_methods || []);
                    setDefaultPaymentMethodId(res.default_payment_method || null);
                    setSelectedPaymentMethodId(res.default_payment_method || (res.payment_methods?.[0]?.id) || null);
                })
                .catch(() => setPaymentMethods([]));
        }
    }, [open, franchise?.stripe_customer_account_id]);

    useEffect(() => {
        if (open && franchise) {
            setAutoRechargeThreshold(franchise.auto_recharge_threshold ?? 50);
            setAutoRechargeAmount(franchise.auto_recharge_amount ?? 300);
        }
    }, [open, franchise]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setClientSecret(null);
            setConfirmedAmount(null);
            setValue(100);
        }
    }, [open]);

    const fetchPaymentIntent = async (amountToCharge: number) => {
        if (!franchise) return;
        setLoadingIntent(true);
        try {
            const response = await GeneralApiService.createLeadPaymentIntent(amountToCharge, false);
            setClientSecret(response.client_secret);
            setConfirmedAmount(amountToCharge);
        } catch (e) {
            toast.error("Could not start Stripe payment. Please try again.");
        } finally {
            setLoadingIntent(false);
        }
    };

    const handleContinueToPayment = () => {
        fetchPaymentIntent(value);
    };

    function closeModal() {
        setOpen(false);
        parentCallback('');
    }

    const validateAutoRecharge = () => {
        const t = Number(autoRechargeThreshold);
        const a = Number(autoRechargeAmount);
        if (t < 10 || t > 1000) {
            toast.error("Threshold must be between £10 and £1,000.");
            return false;
        }
        if (a < 100 || a > 5000) {
            toast.error("Amount must be between £100 and £5,000.");
            return false;
        }
        if (a <= t) {
            toast.error("Bring balance back to must be greater than threshold.");
            return false;
        }
        return true;
    };

    const handleSaveAutoRecharge = (enable: boolean) => {
        if (enable && !selectedPaymentMethodId) {
            toast.error("Please select a payment method.");
            return;
        }
        if (enable && !validateAutoRecharge()) return;
        setSavingAutoRecharge(true);
        GeneralApiService.updateAutoRecharge(
            enable,
            enable ? autoRechargeThreshold : undefined,
            enable ? autoRechargeAmount : undefined,
            enable ? selectedPaymentMethodId ?? undefined : undefined
        )
            .then(() => {
                toast.success(enable ? "Auto Recharge enabled." : "Auto Recharge disabled.");
                onSaved?.();
            })
            .catch((err) => toast.error(err.response?.data?.error || "Failed to update Auto Recharge."))
            .finally(() => setSavingAutoRecharge(false));
    };

    const options = useMemo(() => clientSecret ? {
        clientSecret,
        appearance: {theme: 'stripe' as const}
    } : null, [clientSecret]);

    const showPaymentForm = !loadingIntent && options && confirmedAmount !== null;

    return (
        <>
            <Modal slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'none',
                        backgroundColor: 'rgb(108 108 108 / 60%)',
                        padding: '20px'
                    },
                },
            }} open={open} onClose={() => closeModal()}>
                <ModalDialog
                    sx={{
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        width: { xs: '95vw', sm: 'auto' },
                        maxWidth: { xs: '95vw', sm: '450px' },
                    }}
                >
                    <ModalClose></ModalClose>
                    <DialogTitle>Top up your marketing balance</DialogTitle>
                    <DialogContent sx={{maxWidth: '400px'}}>
                        Choose an amount and pay with a saved card or add a new one.
                    </DialogContent>

                    <Alert
                        variant="soft"
                        color="primary"
                        startDecorator={<Info />}
                        sx={{ mb: 1 }}
                    >
                        <Box>
                            <Typography level="title-sm" sx={{ mb: 0.5 }}>Adding a new card?</Typography>
                            <Typography level="body-sm">
                                To verify your card for future automatic payments, a minimum top-up
                                of <strong>£100</strong> is required. This amount will be credited
                                to your lead balance. Your card will then be saved securely for any
                                future auto-recharge payments.
                            </Typography>
                        </Box>
                    </Alert>

                    {!showPaymentForm && (
                        <>
                            <Slider
                                value={value}
                                onChange={(event: Event, newValue: number | number[]) => {
                                    setValue(newValue as number);
                                }}
                                min={100}
                                max={5000}
                                step={1}
                                valueLabelDisplay="on"
                                valueLabelFormat={formatGBP}
                                sx={{marginTop: 2}}
                            />
                            <DialogActions>
                                <Button
                                    fullWidth
                                    color="primary"
                                    loading={loadingIntent}
                                    onClick={handleContinueToPayment}
                                >
                                    Continue to Payment ({formatGBP(value)})
                                </Button>
                            </DialogActions>
                        </>
                    )}

                    {showPaymentForm && (
                        <Elements stripe={stripePromise} options={options}>
                            <PaymentForm clientSecret={clientSecret} amount={confirmedAmount} onSuccess={closeModal}/>
                        </Elements>
                    )}

                    {paymentMethods.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography level="title-sm" sx={{ mb: 1.5 }}>Auto Recharge</Typography>
                            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                                Automatically top up when your balance goes below a threshold.
                            </Typography>
                            <FormControl size="sm" sx={{ mb: 1.5 }}>
                                <FormLabel>When balance goes below (£)</FormLabel>
                                <Input
                                    type="number"
                                    value={autoRechargeThreshold}
                                    onChange={(e) => setAutoRechargeThreshold(Number(e.target.value) || 0)}
                                    slotProps={{ input: { min: 10, max: 1000, step: 1 } }}
                                />
                            </FormControl>
                            <FormControl size="sm" sx={{ mb: 1.5 }}>
                                <FormLabel>Bring my balance back up to (£)</FormLabel>
                                <Input
                                    type="number"
                                    value={autoRechargeAmount}
                                    onChange={(e) => setAutoRechargeAmount(Number(e.target.value) || 0)}
                                    slotProps={{ input: { min: 100, max: 5000, step: 1 } }}
                                />
                            </FormControl>
                            <FormControl size="sm" sx={{ mb: 2 }}>
                                <FormLabel>Using this payment method</FormLabel>
                                <Select
                                    value={selectedPaymentMethodId}
                                    onChange={(_, v) => setSelectedPaymentMethodId(v)}
                                    placeholder="Select card"
                                >
                                    {paymentMethods.map((pm) => (
                                        <Option key={pm.id} value={pm.id}>
                                            {pm.type === 'link' ? `Link (${pm.email || 'Link'})` : `${pm.brand} ending in ${pm.last4} (Exp. ${pm.exp_month}/${pm.exp_year})`}
                                            {pm.default ? " (default)" : ""}
                                        </Option>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {franchise.recurring_leads_purchase ? (
                                    <>
                                        <Button size="sm" color="primary" loading={savingAutoRecharge} onClick={() => handleSaveAutoRecharge(true)}>
                                            Save
                                        </Button>
                                        <Button size="sm" color="neutral" variant="outlined" loading={savingAutoRecharge} onClick={() => handleSaveAutoRecharge(false)}>
                                            Disable auto recharge
                                        </Button>
                                    </>
                                ) : (
                                    <Button size="sm" color="success" loading={savingAutoRecharge} onClick={() => handleSaveAutoRecharge(true)}>
                                        Enable Auto Recharge
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}

                </ModalDialog>
            </Modal>
        </>
    );
}